/*
 * Marketing route registry — one list, four consumers.
 *
 * Before this file the marketing route list existed in four places that drifted
 * against each other: the <Route> table in App.tsx, the ROUTES array in
 * scripts/prerender.js, public/sitemap.xml, and two path alternations in the
 * pre-paint script in public/index.html. The drift was already real — nine
 * sitemap URLs were never prerendered, and /planners, /personal and /privacy
 * were absent from prerender entirely.
 *
 * Consumers:
 *   1. src/App.tsx              — market-gates its <Route> table against `markets`
 *   2. scripts/prerender.js     — prerenders `prerender: true` routes for the market
 *   3. scripts/generate-seo-files.js — sitemap.xml / llms.txt entries
 *   4. scripts/apply-market.js  — ASSERTS the index.html pre-paint alternation
 *                                 still covers every route (it does not rewrite
 *                                 it; see that file for why)
 *
 * Same CommonJS-in-src/ rules as markets.js: no require(), pure data and pure
 * functions, types in the siteRoutes.d.ts sibling.
 */

/*
 * Dynamic slug sources. Duplicated here rather than imported because the .ts
 * data files cannot be require()d from a build script without a transpile step.
 * src/config/__tests__/siteRoutes.test.ts asserts set-equality against
 * src/data/*.ts and the src/content/blog/*.md filenames, so a drift is a red
 * `make check` rather than a silent 404 or a missing sitemap entry.
 */
const SLUGS = {
    productSlugs: ['quotes', 'travel', 'budget', 'guests', 'automate'],
    scenarioSlugs: ['conferences', 'offsites', 'launches', 'all-hands', 'client-dinners'],
    personaSlugs: ['event-leads', 'heads-of-events', 'operations', 'assistants'],
    compareSlugs: [
        'naboo-alternative',
        'naboo-vs-teamout',
        'teamout-alternative',
        'comtev-vs-naboo',
        'comtev-vs-nowadays',
        'comtev-vs-navan-events',
        'ai-vs-event-management-company',
    ],
    /* Blog slugs are read from the filesystem at build time (see blogSlugs()) so
       adding a post needs no edit here. The test asserts this list stays empty. */
    blogSlugs: [],
};

const BOTH = ['global', 'ng'];

/*
 * crossMarket:
 *   'shared'    — exists on both domains, self-canonical on each, reciprocal
 *                 hreflang. This is the correct treatment for near-identical
 *                 same-language/different-country pages; a cross-domain
 *                 canonical here would tell Google to drop comtev.ng entirely.
 *   'exclusive' — exists on one domain only. No alternate is emitted by either
 *                 side, because an hreflang pointing at a non-existent
 *                 counterpart is worse than none.
 *   'canonical' — served on both, but canonicals to `canonicalMarket`. Used for
 *                 shared editorial so the two domains never compete. A route in
 *                 this class emits NO hreflang: a page that points its canonical
 *                 elsewhere must not also claim to be one side of a pair.
 */
const ROUTES = [
    {
        id: 'home',
        path: '/',
        markets: BOTH,
        prerender: true,
        sitemap: { priority: 1.0, changefreq: 'weekly' },
        crossMarket: 'shared',
        /* NG's home renders the planner-led landing instead of the corporate
           events landing; the component choice lives in App.tsx. */
    },
    {
        id: 'pricing',
        path: '/pricing',
        markets: BOTH,
        prerender: true,
        sitemap: { priority: 0.9, changefreq: 'monthly' },
        crossMarket: 'shared',
    },
    {
        id: 'about',
        path: '/about',
        markets: BOTH,
        prerender: true,
        sitemap: { priority: 0.7, changefreq: 'monthly' },
        crossMarket: 'shared',
    },
    {
        id: 'contact',
        path: '/contact',
        markets: BOTH,
        prerender: true,
        sitemap: { priority: 0.7, changefreq: 'monthly' },
        crossMarket: 'shared',
    },
    {
        id: 'features',
        path: '/features',
        /* GLOBAL ONLY. FeaturesPage is a 28-line stub with no nav, no footer and
           no .lp3 chrome, written in UK corporate voice and styled with app
           classes. It was shipping on comtev.ng looking broken. It stays on .com
           because it is linked and indexed there; Nigeria gets /how-it-works
           instead, which does the same job properly. */
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.7, changefreq: 'monthly' },
        crossMarket: 'shared',
    },
    {
        id: 'partners',
        path: '/partners',
        /* GLOBAL ONLY (2026-09-03). PartnersPage is the UK corporate partner
           pitch ("Comtev's AI planner sources venues and vendors for every
           event", "no listing fees") in lp3 chrome. On comtev.ng it shipped in
           the sitemap and the nav JSON-LD while making claims the NG product
           does not (seo/ng/site-audit-2026-09-03.md). Bring it back with an NG
           page of its own, not this one. */
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.6, changefreq: 'monthly' },
        crossMarket: 'exclusive',
    },
    {
        id: 'privacy',
        path: '/privacy',
        markets: BOTH,
        prerender: true,
        sitemap: { priority: 0.3, changefreq: 'yearly' },
        crossMarket: 'shared',
        /* FLAG: this is a UK-GDPR policy. Serving it unchanged on comtev.ng does
           not meet NDPA 2023 / NDPR expectations (named controller, Nigerian
           contact route). Legal review is launch-blocking — see the plan. */
    },
    {
        /* NG only. comtev.com explains itself through /features and the product
           pages; Nigeria has neither, so this is where the whole run of one
           event gets told: set it up, collect guests, invite, door, invoice. */
        id: 'howItWorks',
        path: '/how-it-works',
        markets: ['ng'],
        prerender: true,
        sitemap: { priority: 0.9, changefreq: 'monthly' },
        crossMarket: 'exclusive',
    },
    {
        /* NG only. A free client-side naira invoice builder: "invoice generator"
           is 6,600 searches a month in Nigeria with Low competition and no
           local page but Refrens (seo/ng/strategy-ng-2026-09-03.md, pool E).
           The hand-off at the bottom is Comtev invoicing with Paystack. */
        id: 'invoiceGenerator',
        path: '/invoice-generator',
        markets: ['ng'],
        prerender: true,
        sitemap: { priority: 0.8, changefreq: 'monthly' },
        crossMarket: 'exclusive',
    },
    {
        id: 'terms',
        path: '/terms',
        markets: BOTH,
        prerender: true,
        sitemap: { priority: 0.3, changefreq: 'yearly' },
        crossMarket: 'shared',
        /* FLAG: the .com terms describe a paid per-event product. Nigeria is
           free to run, with the planner reselling sends and seats at their own
           price, so the two markets describe materially different agreements.
           Same review as /privacy. */
    },

    /* ── The corporate-events surfaces — GLOBAL ONLY, for now ─────────────────
       These five are `markets: ['global']` for two independent reasons, either
       of which alone would be enough:

       1. CONTENT. They are written for a UK corporate buyer, and not just in
          currency: "flights from London and Manchester, trains from Leeds", "two
          nights in central London this October", a testimonial from the
          University of Manchester. Serving that to a Nigerian visitor is worse
          than not serving it.
       2. UNSIGNED FIGURES. /products/:slug and /use-cases/:slug render the demo
          widgets, whose NG numbers are still PENDING_SIGN_OFF in
          src/data/demoFigures.ts (see `usesDemoFigures` below).

       Nigeria therefore launches planner-led: '/', pricing, about, contact,
       features, partners, privacy and the shared blog.

       TO BRING THEM BACK: change `markets` to BOTH on the entries you want, and
       — for the two flagged `usesDemoFigures` — sign off the ngn figure set
       first, or the build will stop. Nothing else needs touching: the router,
       prerender list, sitemap and nav all follow this file. */
    {
        id: 'products',
        path: '/products',
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.8, changefreq: 'monthly' },
        crossMarket: 'shared',
    },
    {
        id: 'product',
        path: '/products/:slug',
        dynamic: 'productSlugs',
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.7, changefreq: 'monthly' },
        crossMarket: 'shared',
        usesDemoFigures: true,
    },
    {
        id: 'useCases',
        path: '/use-cases',
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.8, changefreq: 'monthly' },
        crossMarket: 'shared',
    },
    {
        id: 'scenario',
        path: '/use-cases/:slug',
        dynamic: 'scenarioSlugs',
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.7, changefreq: 'monthly' },
        crossMarket: 'shared',
        usesDemoFigures: true,
    },
    {
        id: 'persona',
        path: '/for/:slug',
        dynamic: 'personaSlugs',
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.7, changefreq: 'monthly' },
        crossMarket: 'shared',
    },

    /* ── Editorial — PER-MARKET CONTENT on a shared path ──────────────────────
       Since the NG rebrand, the two domains carry DISJOINT post sets: a post's
       frontmatter `market` field scopes it (absent = global-only in practice,
       'ng' = comtev.ng only). Each domain's /blog and /blog/:slug are therefore
       self-canonical with no hreflang pair — which is exactly what
       'exclusive' expresses. The old crossMarket:'canonical' → global treatment
       died with the shared list: the UK-costed corporate posts no longer render,
       prerender or list on comtev.ng at all (they 404 there), and the NG guides
       do the same on comtev.com. Consumers that enforce this: BlogIndexPage /
       BlogArticlePage (global chrome), src/pages/ng/NgBlog pages (ng chrome),
       scripts/prerender.js and scripts/generate-seo-files.js (both filter the
       slug lists per market). */
    {
        id: 'blogIndex',
        path: '/blog',
        markets: BOTH,
        prerender: true,
        sitemap: { priority: 0.6, changefreq: 'weekly' },
        crossMarket: 'exclusive',
    },
    {
        id: 'blogArticle',
        path: '/blog/:slug',
        dynamic: 'blogSlugs',
        markets: BOTH,
        prerender: true,
        sitemap: { priority: 0.6, changefreq: 'monthly' },
        crossMarket: 'exclusive',
    },

    /* ── Global-only ──────────────────────────────────────────────────────────
       The three UK money pages are built on GBP claims and UK cost benchmarks;
       they must not exist on a .ng domain in any form. /personal is the weddings
       app, kept to a footer link per marketing-claims-guardrails. */
    {
        id: 'aiEventPlannerUk',
        path: '/ai-event-planner-uk',
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.9, changefreq: 'monthly' },
        crossMarket: 'exclusive',
    },
    {
        id: 'corporateEventCostUk',
        path: '/corporate-event-cost-uk',
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.9, changefreq: 'monthly' },
        crossMarket: 'exclusive',
    },
    {
        id: 'bestAiEventToolsUk',
        path: '/best-ai-event-planning-tools-uk',
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.9, changefreq: 'monthly' },
        crossMarket: 'exclusive',
    },
    /* ── The compete pages (seo/strategy-2026-10-13.md, round 2) ─────────────
       Four static money pages that take the search terms a UK buyer uses when
       they are about to hire a venue finder, a corporate event planner or an
       event management company. GBP claims and UK comparisons: global only.
       Records live in src/data/competePages.ts; paths are static so no slug
       parity entry is needed. */
    {
        id: 'venueFinder',
        path: '/venue-finder',
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.9, changefreq: 'monthly' },
        crossMarket: 'exclusive',
    },
    {
        id: 'venueFinderLondon',
        path: '/venue-finder/london',
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.8, changefreq: 'monthly' },
        crossMarket: 'exclusive',
    },
    {
        id: 'corporateEventPlanner',
        path: '/corporate-event-planner',
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.9, changefreq: 'monthly' },
        crossMarket: 'exclusive',
    },
    {
        id: 'eventManagementCompany',
        path: '/event-management-company',
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.9, changefreq: 'monthly' },
        crossMarket: 'exclusive',
    },
    {
        /* The comparison cluster (seo/strategy-2026-09-02.md). GBP claims and
           UK-market comparisons, so global-only like the money pages. Slugs
           mirror src/data/compare.ts; the parity test guards the duplication. */
        id: 'compare',
        path: '/compare/:slug',
        dynamic: 'compareSlugs',
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.8, changefreq: 'monthly' },
        crossMarket: 'exclusive',
    },
    {
        id: 'personal',
        path: '/personal',
        markets: ['global'],
        prerender: true,
        sitemap: { priority: 0.4, changefreq: 'monthly' },
        crossMarket: 'exclusive',
    },

    /* ── The planner-reseller offering ────────────────────────────────────────
       ₦ pricing, WhatsApp-first, the planner sets their own client price. It is
       already Nigeria-shaped, which is why NG LEADS with it: on the ng build it
       is served at '/' (see the HomePage fork in App.tsx), not here.

       So '/planners' is deliberately global-only. Serving the same component at
       both '/' and '/planners' on ng would put two identical, self-canonical
       pages on a brand-new domain — the cheapest possible way to get it read as
       a thin duplicate.

       On global this entry preserves exactly today's behaviour: the route
       resolves, but it is unlinked, unprerendered and absent from the sitemap.
       Its MONEY block carries an owner-sign-off rule — respect it. */
    {
        id: 'planners',
        path: '/planners',
        markets: ['global'],
        prerender: false,
        sitemap: false,
        crossMarket: 'exclusive',
    },
];

/* Extra first-path-segments that appear in the index.html pre-paint alternation
   but are not marketing routes. '/l/' is a public token link that bounces to the
   app; it is listed there so the cream marketing background paints during the
   bounce rather than flashing dark. */
const EXTRA_LIGHT_SEGMENTS = ['l'];

function routesForMarket(marketId) {
    return ROUTES.filter((route) => route.markets.indexOf(marketId) !== -1);
}

function findRoute(id) {
    return ROUTES.find((route) => route.id === id);
}

/*
 * Every concrete path for a market, with :slug routes expanded.
 * `dynamicSlugs` lets a caller inject filesystem-derived slugs (the blog) that
 * the static SLUGS table deliberately does not hardcode.
 */
function expandedPathsForMarket(marketId, dynamicSlugs) {
    const slugs = Object.assign({}, SLUGS, dynamicSlugs || {});
    const out = [];

    routesForMarket(marketId).forEach((route) => {
        if (!route.dynamic) {
            out.push({ path: route.path, route });
            return;
        }
        (slugs[route.dynamic] || []).forEach((slug) => {
            out.push({ path: route.path.replace(':slug', slug), route });
        });
    });

    return out;
}

/*
 * First path segments a market's routes occupy. Used to ASSERT the pre-paint
 * alternation in public/index.html still covers everything — see
 * scripts/apply-market.js.
 */
function lightMarketingSegments(marketId) {
    const segments = routesForMarket(marketId)
        .map((route) => route.path.split('/')[1])
        .filter(Boolean);

    return Array.from(new Set(segments.concat(EXTRA_LIGHT_SEGMENTS))).sort();
}

/*
 * The canonical URL for a path in a market. Returns another market's origin for
 * `crossMarket: 'canonical'` routes, which is what keeps the shared blog from
 * competing with itself across the two domains.
 */
function routeForPath(path) {
    /* Exact match first so '/blog' resolves to the index, not the article
       pattern, and '/products' does not match '/products/:slug'. */
    const exact = ROUTES.find((r) => !r.dynamic && r.path === path);
    if (exact) return exact;

    return ROUTES.find((r) => {
        if (!r.dynamic) return false;
        const prefix = r.path.replace(':slug', '');
        return path.indexOf(prefix) === 0 && path.length > prefix.length;
    });
}

function canonicalOriginFor(markets, marketId, path) {
    const route = routeForPath(path);

    const owner =
        route && route.crossMarket === 'canonical' && route.canonicalMarket
            ? route.canonicalMarket
            : marketId;

    return markets[owner].siteUrl;
}

function canonicalUrlFor(markets, marketId, path) {
    return `${canonicalOriginFor(markets, marketId, path)}${path}`;
}

module.exports = {
    ROUTES,
    SLUGS,
    EXTRA_LIGHT_SEGMENTS,
    routesForMarket,
    findRoute,
    routeForPath,
    expandedPathsForMarket,
    lightMarketingSegments,
    canonicalOriginFor,
    canonicalUrlFor,
};
